import { ArtifactFormComponentProps } from '@/features/artifacts/components/ArtifactForm';
import { FieldProxy } from '@/features/artifacts/components/ArtifactForm/ArtifactForm.fields';
import { ArtifactFormOverrides } from '@/features/artifacts/components/ArtifactForm/ArtifactForm.types';
import { useArtifactFormConfig } from '@/features/artifacts/hooks/useArtifactFormConfig';
import ArtifactFormProvider from '@/features/artifacts/providers/ArtifactFormProvider';
import { useArtifactFormContext } from '@/features/artifacts/providers/ArtifactFormProvider.context';
import {
  setMedicamentoFromConcentracoes,
  supportingInfoFhirPath,
} from '@/features/fhir/helpers/supportingInfo';
import fhirpath from 'fhirpath';
import React, { useMemo } from 'react';
import { BaseForm } from './styles';

export interface FormMedicamentoProps extends ArtifactFormComponentProps {
  withConcentracoes?: boolean;
}

const FormMedicamentoComSubstituicoes: React.FC<ArtifactFormComponentProps> = ({
  resource,
  onSubmit,
  onCancel,
  disabled = false,
  loading = false,
  readOnly = false,
}) => {
  const { handleResourceSubmit, reset, form } = useArtifactFormContext();

  React.useEffect(() => {
    if (resource) {
      reset();
    }
  }, [resource, reset]);

  return (
    <BaseForm
      form={form}
      loading={loading}
      disabled={disabled}
      readOnly={readOnly}
      onCancel={() => onCancel(resource)}
      onSubmit={handleResourceSubmit(onSubmit)}
    />
  );
};

const FormMedicamento: React.FC<FormMedicamentoProps> = ({
  resource,
  onSubmit,
  onCancel,
  withConcentracoes = false,
  disabled = false,
  loading = false,
  readOnly = false,
}) => {
  const defaultFormConfig = useArtifactFormConfig('prescricao-medica', [
    {
      name: 'dosageInstruction',
      label: 'Posologia',
      required: true,
      attrs: { autoFocus: true },
    },
  ] as ArtifactFormOverrides);

  const setDosageInstruction = (src: fhir4.FhirResource, value: string) => {
    const medicationRequest = src as fhir4.MedicationRequest;

    const supportInfoId = fhirpath.evaluate(
      medicationRequest,
      `${supportingInfoFhirPath}.id`,
    )[0] as string;

    return setMedicamentoFromConcentracoes(
      medicationRequest,
      supportInfoId,
      value,
    );
  };

  const readOnlyFhirEvalue = useMemo(() => {
    const URL =
      'http://healthit.medflowapp/fhir/StructureDefinition/is-dosage-instruction-autogenerated';
    const isAutoGeneretad = fhirpath.evaluate(
      resource,
      `dosageInstruction.extension.where(url='${URL}').valueBoolean.first()`,
    )[0] as boolean;
    return isAutoGeneretad;
  }, [resource]);

  const getFormConfig = () => {
    if (!readOnly && withConcentracoes) {
      return [
        FieldProxy({
          name: 'dosageInstruction',
          label: 'Posologia',
          propertyPath: 'dosageInstruction',
          fhirPath: `${supportingInfoFhirPath}.dosageInstruction.text`,
          setValue: setDosageInstruction,
          widget: 'short_answer',
          attrs: { autoFocus: true },
          readOnly: readOnlyFhirEvalue,
        }),
      ];
    }

    return [
      {
        ...defaultFormConfig[0],
        attrs: { ...defaultFormConfig[0].attrs, readOnly: readOnlyFhirEvalue },
      },
    ];
  };

  return (
    <ArtifactFormProvider form={getFormConfig()} resource={resource}>
      <FormMedicamentoComSubstituicoes
        resource={resource}
        onSubmit={onSubmit}
        onCancel={onCancel}
        disabled={disabled}
        loading={loading}
        readOnly={readOnly}
      />
    </ArtifactFormProvider>
  );
};

export default FormMedicamento;
